import React from 'react';
import { shuffle } from 'lodash';
import { graphql } from 'gatsby';
import BaseLayout from '../components/layout/BaseLayout';
import SEO from '../components/SEO';
import MarkdownTemplate from './MarkdownTemplate';
import { MoreArticles } from '../components/layout/MoreArticles';

const getDocSlug = (fileAbsolutePath) => {
  const filename = fileAbsolutePath.split('/').slice(-1)[0] || '';
  const slug = filename.replace('.md', '');
  return slug === 'home' ? '' : slug;
};

const normalizeNavSlug = (slug) => {
  if (!slug) {
    return '';
  }

  const trimmed = slug.replace(/^\/+/, '');
  return trimmed.split('#')[0];
};

const findCategoryBySlug = (categories, docSlug) =>
  categories.find((category) =>
    category.items.some((item) => {
      if (normalizeNavSlug(item.slug) === docSlug) {
        return true;
      }

      return item.items && item.items.some((subitem) => normalizeNavSlug(subitem.slug) === docSlug);
    })
  );

const DocsTemplate = ({
  data,
  pageContext: { locale, section, category, fileAbsolutePath },
  location,
}) => {
  // Navigation items
  const navItems = data.allYaml.edges[0].node.nav.categories;

  // Current post
  const {
    html,
    rawMarkdownBody,
    frontmatter: { title, hidden },
  } = data.allMarkdownRemark.edges.find(
    (edge) => edge.node.fileAbsolutePath === fileAbsolutePath
  ).node;

  // Poor man's related posts algorithm
  const relatedItems = shuffle(
    data.allMarkdownRemark.edges
      .filter(
        (edge) =>
          !edge.node.frontmatter.hidden &&
          edge.node.fileAbsolutePath.includes(locale) &&
          edge.node.fileAbsolutePath.includes(section) &&
          !edge.node.fileAbsolutePath.includes(fileAbsolutePath)
      )
      .map((edge) => ({
        link: `/${section}/${edge.node.fileAbsolutePath
          .split('/')
          .slice(-1)[0]
          .split('.')[0]
          .replace('home', '')}`.replace(/\/$/, ''),
        heading: edge.node.frontmatter.title,
        text: edge.node.frontmatter.excerpt,
      }))
  ).splice(0, 3);

  const docSlug = getDocSlug(fileAbsolutePath);
  const matchedCategory = findCategoryBySlug(navItems, docSlug);
  const initialSidebarCategory =
    matchedCategory?.slug ||
    navItems.find((navItem) => navItem.slug === category)?.slug ||
    navItems[0]?.slug;

  return (
    <BaseLayout
      locale={locale}
      render={() => (
        <>
          <SEO
            title={title}
            locale={locale}
            pathname={location.pathname}
            meta={hidden ? [{ name: 'robots', content: 'noindex, nofollow' }] : []}
          />
          <MarkdownTemplate
            section={section}
            sidebarCategories={navItems}
            initialSidebarCategory={initialSidebarCategory}
            html={html}
            markdown={rawMarkdownBody}
          />
        </>
      )}
      renderRelated={() => <MoreArticles items={relatedItems} />}
    />
  );
};

export const query = graphql`
  query ($section: String!) {
    allMarkdownRemark {
      edges {
        node {
          fileAbsolutePath
          frontmatter {
            title
            excerpt
            hidden
          }
          html
          rawMarkdownBody
        }
      }
    }
    allYaml(filter: { nav: { section: { eq: $section } } }) {
      edges {
        node {
          nav {
            categories {
              slug
              translationKey
              items {
                slug
                translationKey
                items {
                  slug
                  translationKey
                }
              }
            }
          }
        }
      }
    }
  }
`;

export default DocsTemplate;
